
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMentorForm } from "@/hooks/useMentorForm";
import MentorFormStep1 from "@/components/mentors/MentorFormStep1";
import MentorFormStep2 from "@/components/mentors/MentorFormStep2";
import MentorFormStep3 from "@/components/mentors/MentorFormStep3";
import MentorFormProgress from "@/components/mentors/MentorFormProgress";
import { useMentorProfile } from "@/hooks/profile/useMentorProfile";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const BecomeMentor = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { form, step, nextStep, prevStep, isSubmitting, onSubmit } = useMentorForm();
  const { mentorProfile, loading: loadingProfile, fetchMentorProfile } = useMentorProfile();
  const [isVerified, setIsVerified] = useState(false);
  
  // Check if user is already a mentor
  useEffect(() => {
    const checkMentorStatus = async () => {
      const profile = await fetchMentorProfile();
      if (profile) {
        setIsVerified(profile.status === 'approved');
      }
    };
    
    checkMentorStatus();
  }, [fetchMentorProfile]);
  
  // If verified, show success message and redirect to dashboard
  if (isVerified) {
    setTimeout(() => {
      navigate("/dashboard");
      toast({
        title: "You are now a Mentor!",
        description: "You can now start helping teens as a mentor."
      });
    }, 3000);
    
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">You are now a Mentor!</h1>
          <p className="text-gray-600 mb-6">
            Redirecting you to your dashboard where you can complete your mentor profile.
          </p>
          <Button onClick={() => navigate("/dashboard")} className="w-full">
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }
  
  const handleFormSubmit = () => {
    onSubmit(form.getValues());
  };
  
  return (
    <div className="container mx-auto py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">Become a Mentor</h1>
        <p className="text-muted-foreground mb-8">
          Share your knowledge and experience to help teens find their path.
        </p>
        
        <MentorFormProgress step={step} totalSteps={3} />
        
        <div className="mt-8 bg-card rounded-lg border shadow-sm p-6">
          {step === 1 && (
            <MentorFormStep1 form={form} />
          )}
          
          {step === 2 && (
            <MentorFormStep2 form={form} />
          )}
          
          {step === 3 && (
            <MentorFormStep3 form={form} />
          )}
          
          <div className="flex justify-between mt-10">
            <Button 
              type="button" 
              variant="outline" 
              onClick={prevStep} 
              disabled={step === 1 || isSubmitting}
            >
              Previous
            </Button>
            
            {step < 3 ? (
              <Button type="button" onClick={nextStep}>
                Continue
              </Button>
            ) : (
              <Button 
                onClick={handleFormSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit Application"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BecomeMentor;
